import {useCallback, useEffect, useState} from "react";
import {loadPropertyRules} from "../../service/AutoAssignService";
import {hasWorker} from "../../service/jobScheduling";
import {isTaskJob} from "./schedulerUtils";

// Loads the per-property cleaner rules for the given regions. Callers pass only regions the
// signed-in Admin may plan, so a non-Admin session never issues these requests.
export default function usePropertyCleanerRules(regions = []) {
    const [rulesByProperty, setRulesByProperty] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const key = regions.join(",");

    const refresh = useCallback(async () => {
        const values = key ? key.split(",") : [];
        if (values.length === 0) {
            setRulesByProperty({});
            setErrorMessage("");
            return;
        }
        const results = await Promise.allSettled(values.map(region => loadPropertyRules(region)));
        const next = {};
        results.forEach(result => {
            if (result.status !== "fulfilled") return;
            result.value.forEach(rule => { next[String(rule.property_id)] = rule; });
        });
        setRulesByProperty(next);
        setErrorMessage(results.some(result => result.status === "rejected") ? "Unable to load property cleaner rules." : "");
    }, [key]);

    useEffect(() => {
        let active = true;
        refresh().catch(error => { if (active) setErrorMessage(error.message || "Unable to load property cleaner rules."); });
        return () => { active = false; };
    }, [refresh]);

    const updateRule = useCallback(rule => {
        setRulesByProperty(current => ({...current, [String(rule.property_id)]: rule}));
    }, []);

    return {rulesByProperty, refresh, updateRule, errorMessage};
}

export function workerLabel(entry, id) {
    if (!entry) return `Worker ${id}`;
    return entry.isDeleted ? `${entry.name} (Inactive)` : entry.name;
}

// `directory` maps worker id to a directory entry ({name, region, isDeleted}) from UserService.toFieldWorker.
export function cleanerHint(rule, directory = {}) {
    if (!rule?.preferred_worker_id) return null;
    const workerId = String(rule.preferred_worker_id);
    const mode = rule.assignment_mode === "FIXED" ? "FIXED" : "PREFERRED";
    const entry = directory[workerId];
    const backupId = rule.backup_worker_id ? String(rule.backup_worker_id) : null;
    const workerName = workerLabel(entry, workerId);
    return {
        workerId, mode, backupId, workerName,
        backupName: backupId ? workerLabel(directory[backupId], backupId) : null,
        // A cleaner missing from the directory, or inactive, cannot receive one-click assignments.
        assignable: Boolean(entry) && !entry.isDeleted,
        label: `${mode === "FIXED" ? "Fixed" : "Preferred"}: ${workerName}`,
    };
}

export function isUnassignedCleaningJob(job) {
    return !isTaskJob(job) && job.status !== "Cancelled" && job.status !== "ReadyForBilling"
        && !hasWorker(job);
}
